import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import {
	Panel,
	PanelGroup,
	PanelResizeHandle,
	type ImperativePanelGroupHandle,
	type ImperativePanelHandle,
} from "react-resizable-panels";
import { savedLeftPaneSize, useLayoutStore, type MainView } from "@/stores/useLayoutStore";
import { WORKSPACE_MIN_SIZE } from "@/stores/layout-state";
import { toMinSizePercent, usePanelGroupSize } from "@/lib/panel-min-size";
import { AgentSessionProvider } from "@/components/chat/AgentSessionContext";
import { ChatUrlSync } from "@/components/chat/ChatUrlSync";
import { FileUrlSync } from "@/components/chat/FileUrlSync";
import { BillingPage } from "@/components/billing/BillingPage";
import { VolumesPage } from "@/components/volumes/VolumesPage";
import { OrganizationPage } from "@/components/admin/OrganizationPage";
import { RunnersTab } from "@/components/admin/RunnersTab";
import { ProjectsPage } from "@/components/projects/ProjectsPage";
import { QuickOpenDialog } from "@/components/quick-open/QuickOpenDialog";
import { MainViewUrlSync } from "./MainViewUrlSync";
import { Sidebar } from "./Sidebar";
import { LeftPane } from "./LeftPane";
import { CenterPane, CHAT_COLUMN_MIN_WIDTH } from "./CenterPane";
import { RightPane } from "./RightPane";
import { BottomPane, BottomPaneTabBar } from "./BottomPane";
import { useChatMonitorsSync } from "@/components/monitor/useChatMonitorsSync";
import { useChatEnvironmentStatusSync } from "@/components/chat/useChatEnvironmentStatusSync";
import { useNewMonitorFocus } from "./useNewMonitorFocus";
import { useSegmentationPaneSync } from "./useSegmentationPaneSync";
import { useRightPaneVisibility } from "./useRightPaneVisibility";
import { ShortcutHelpDialog } from "@/components/common/ShortcutHelpDialog";
import { useGlobalShortcuts } from "@/lib/useGlobalShortcuts";

export function AppLayout() {
	useGlobalShortcuts();
	// リサイズ中の onLayout 更新で再レンダーしないよう、初期サイズはマウント時に一度だけ読む。
	const [initialSizes] = useState(() => {
		const state = useLayoutStore.getState();
		return {
			workspace: state.workspaceSizes,
			centerRight: state.centerRightSizes,
		};
	});

	const [defaultLeft, defaultWorkspace] = initialSizes.workspace;
	const [defaultCenter, defaultRight] = initialSizes.centerRight;

	return (
		<AgentSessionProvider>
			<ChatUrlSync />
			<FileUrlSync />
			<MainViewUrlSync />
			<ShortcutHelpDialog />
			<QuickOpenDialog />
			<div
				data-testid="app-layout"
				className="h-screen w-screen overflow-hidden bg-surface-1"
				style={{ display: "flex" }}
			>
				<Sidebar />
				<MainViewSwitcher
					chatView={
						<WorkspacePanelGroup
							defaultLeft={defaultLeft}
							defaultWorkspace={defaultWorkspace}
							left={<LeftPane />}
							workspace={
								<WorkspacePanels defaultCenter={defaultCenter} defaultRight={defaultRight} />
							}
						/>
					}
					views={{
						projects: <ProjectsPage />,
						volumes: <VolumesPage />,
						billing: <BillingPage />,
						organization: <OrganizationPage />,
						devices: <RunnersTab />,
					}}
				/>
			</div>
		</AgentSessionProvider>
	);
}

/** 左ペインの最小幅 (px)。これより狭くドラッグすると閉じる。 */
const LEFT_PANE_MIN_WIDTH = 180;

/** グループ幅を測り終えるまで使う左ペインの下限 (%)。初回描画の 1 フレームだけ効く。 */
const LEFT_PANE_MIN_SIZE_FALLBACK = 10;

interface WorkspacePanelGroupProps {
	defaultLeft: number;
	defaultWorkspace: number;
	/** 左ペインとワークスペースの中身。実測のたびに作り直さないよう、要素のまま受け取る。 */
	left: ReactNode;
	workspace: ReactNode;
}

/**
 * 左ペインとワークスペースの左右分割。グループの実寸を測って左ペインの下限を px で保つ。
 * 下限が % のままだと、広いディスプレイでは下限の実寸が太って畳みにくく、狭い
 * ディスプレイでは中身が読めない幅まで潰せてしまう。
 */
function WorkspacePanelGroup({
	defaultLeft,
	defaultWorkspace,
	left,
	workspace,
}: WorkspacePanelGroupProps) {
	const setWorkspaceSizes = useLayoutStore((s) => s.setWorkspaceSizes);
	const groupRef = useRef<ImperativePanelGroupHandle>(null);
	const { ref, size: groupWidth } = usePanelGroupSize("horizontal");
	const leftMinSize = toMinSizePercent(
		LEFT_PANE_MIN_WIDTH,
		groupWidth,
		LEFT_PANE_MIN_SIZE_FALLBACK,
	);

	// サイドバーの開閉やウィンドウのリサイズでは比率が変わらず onLayout も来ないため、
	// 保存済みの実寸だけが古くなる。幅が変わったら今の比率と組にして測り直す。
	useEffect(() => {
		if (groupWidth <= 0) return;
		const layout = groupRef.current?.getLayout();
		if (layout) setWorkspaceSizes(layout, groupWidth);
	}, [groupWidth, setWorkspaceSizes]);

	return (
		<div ref={ref} style={{ height: "100%", minWidth: 0 }}>
			<PanelGroup
				ref={groupRef}
				direction="horizontal"
				onLayout={(sizes) => setWorkspaceSizes(sizes, groupWidth)}
			>
				<LeftPanel defaultSize={defaultLeft} minSize={leftMinSize} groupWidth={groupWidth}>
					{left}
				</LeftPanel>
				<ResizeHandle />
				<Panel defaultSize={defaultWorkspace} minSize={WORKSPACE_MIN_SIZE}>
					{workspace}
				</Panel>
			</PanelGroup>
		</div>
	);
}

interface LeftPanelProps {
	defaultSize: number;
	minSize: number;
	/** 左右分割グループの実寸 (px)。0 は測定前。 */
	groupWidth: number;
	children: ReactNode;
}

/**
 * 左ペイン (環境ファイル / チャット) の Panel。ハンドルを左へドラッグして最小幅より
 * 狭めると閉じ、サイドバーのチャットアイコンで開き直せる。開閉状態の購読をここに
 * 閉じ込め、トグルのたびに AppLayout ごとチャットビューが再レンダーされるのを防ぐ。
 */
function LeftPanel({ defaultSize, minSize, groupWidth, children }: LeftPanelProps) {
	const panelRef = useRef<ImperativePanelHandle>(null);
	const collapsed = useLayoutStore((s) => s.leftPaneCollapsed);
	const setLeftPaneCollapsed = useLayoutStore((s) => s.setLeftPaneCollapsed);
	// 閉じた状態はドラッグでもサイドバーからでも変わるため、マウント時の値だけを既定幅に使う。
	const [initiallyCollapsed] = useState(() => useLayoutStore.getState().leftPaneCollapsed);
	const widthRestored = useRef(false);

	// 保存時と実寸の違うディスプレイでは、比率ではなく幅 (px) を保って復元する。
	// 実測はマウント後の測定で入るため、幅が判明した最初の 1 回だけ描画前に補正する。
	// 閉じている間は実寸を当てられないため、復元は開き直すときまで持ち越す。
	useLayoutEffect(() => {
		if (widthRestored.current || groupWidth <= 0) return;
		if (useLayoutStore.getState().leftPaneCollapsed) return;
		widthRestored.current = true;
		panelRef.current?.resize(savedLeftPaneSize(groupWidth, minSize));
	}, [groupWidth, minSize]);

	useEffect(() => {
		const panel = panelRef.current;
		if (!panel) return;
		if (collapsed) panel.collapse();
		// ドラッグで開いたときは今の幅を尊重し、サイドバーから開いたときだけ元の幅へ戻す。
		else if (panel.isCollapsed()) {
			widthRestored.current = true;
			panel.resize(savedLeftPaneSize(groupWidth, minSize));
		}
	}, [collapsed, groupWidth, minSize]);

	return (
		<Panel
			ref={panelRef}
			defaultSize={initiallyCollapsed ? 0 : defaultSize}
			minSize={minSize}
			collapsible
			collapsedSize={0}
			onCollapse={() => setLeftPaneCollapsed(true)}
			onExpand={() => setLeftPaneCollapsed(false)}
		>
			{children}
		</Panel>
	);
}

function WorkspacePanels({
	defaultCenter,
	defaultRight,
}: {
	defaultCenter: number;
	defaultRight: number;
}) {
	useChatMonitorsSync();
	useChatEnvironmentStatusSync();
	useNewMonitorFocus();
	useSegmentationPaneSync();
	const bottomPanelRef = useRef<ImperativePanelHandle>(null);
	const bottomPaneOpen = useLayoutStore((state) => state.bottomPaneOpen);
	const bottomPaneHeight = useLayoutStore((state) => state.bottomPaneHeight);
	const setBottomPaneHeight = useLayoutStore((state) => state.setBottomPaneHeight);
	const setBottomPaneOpen = useLayoutStore((state) => state.setBottomPaneOpen);

	useEffect(() => {
		if (bottomPaneOpen) bottomPanelRef.current?.resize(bottomPaneHeight);
		else bottomPanelRef.current?.collapse();
	}, [bottomPaneHeight, bottomPaneOpen]);

	return (
		<div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
			<div style={{ flex: 1, minHeight: 0 }}>
				<PanelGroup direction="vertical">
					<Panel defaultSize={bottomPaneOpen ? 100 - bottomPaneHeight : 100} minSize={40}>
						<CenterRightPanels
							defaultCenter={defaultCenter}
							defaultRight={defaultRight}
							center={<CenterPane />}
							right={<RightPane />}
						/>
					</Panel>
					{/* 高さ変更のつまみは BottomPaneTabBar がタブ列全体に敷くため、ここには置かない。 */}
					<Panel
						ref={bottomPanelRef}
						defaultSize={bottomPaneOpen ? bottomPaneHeight : 0}
						minSize={15}
						maxSize={60}
						collapsedSize={0}
						collapsible
						onCollapse={() => setBottomPaneOpen(false)}
						onExpand={() => setBottomPaneOpen(true)}
						onResize={(size) => {
							if (size >= 15) setBottomPaneHeight(size);
						}}
					>
						<BottomPane />
					</Panel>
				</PanelGroup>
			</div>
			{/* 折りたたみ中は親タブ列だけを画面下端に残す (展開中は BottomPane の内側が描画する)。 */}
			{!bottomPaneOpen && <BottomPaneTabBar />}
		</div>
	);
}

/** 中央ペインの最小幅 (px)。コンポーザーの操作列がはみ出さない下限に合わせる。 */
const CENTER_PANE_MIN_WIDTH = CHAT_COLUMN_MIN_WIDTH;

/** 右ペイン (エディタ) の最小幅 (px)。 */
const RIGHT_PANE_MIN_WIDTH = 240;

/** グループ幅を測り終えるまで使う下限 (%)。初回描画の 1 フレームだけ効く。 */
const CENTER_PANE_MIN_SIZE_FALLBACK = 30;
const RIGHT_PANE_MIN_SIZE_FALLBACK = 15;

interface CenterRightPanelsProps {
	defaultCenter: number;
	defaultRight: number;
	/** 中央と右の中身。実測のたびに作り直さないよう、要素のまま受け取る。 */
	center: ReactNode;
	right: ReactNode;
}

/**
 * チャットとエディタの左右分割。両者の下限をグループの実寸から px で保つ。
 * 右ペインはファイルに合わせて自動で開閉し、ハンドルのドラッグでも開閉できる。
 */
function CenterRightPanels({ defaultCenter, defaultRight, center, right }: CenterRightPanelsProps) {
	const setCenterRightSizes = useLayoutStore((s) => s.setCenterRightSizes);
	const { ref, size: groupWidth } = usePanelGroupSize("horizontal");
	const rightPanelRef = useRef<ImperativePanelHandle>(null);
	const rightPane = useRightPaneVisibility(rightPanelRef);

	return (
		<div ref={ref} style={{ height: "100%", minWidth: 0 }}>
			<PanelGroup direction="horizontal" onLayout={setCenterRightSizes}>
				<Panel
					defaultSize={rightPane.initiallyCollapsed ? 100 : defaultCenter}
					minSize={toMinSizePercent(
						CENTER_PANE_MIN_WIDTH,
						groupWidth,
						CENTER_PANE_MIN_SIZE_FALLBACK,
					)}
				>
					{center}
				</Panel>
				<ResizeHandle />
				<Panel
					ref={rightPanelRef}
					defaultSize={rightPane.initiallyCollapsed ? 0 : defaultRight}
					minSize={toMinSizePercent(RIGHT_PANE_MIN_WIDTH, groupWidth, RIGHT_PANE_MIN_SIZE_FALLBACK)}
					collapsible
					collapsedSize={0}
					onCollapse={rightPane.onCollapse}
					onExpand={rightPane.onExpand}
				>
					{right}
				</Panel>
			</PanelGroup>
		</div>
	);
}

interface MainViewSwitcherProps {
	chatView: React.ReactNode;
	/** チャット以外のビュー。選択中のものだけをマウントする。 */
	views: Record<Exclude<MainView, "chats">, React.ReactNode>;
}

/**
 * メイン領域に表示するビューを `useLayoutStore.mainView` に応じて切り替える。
 *
 * `mainView` の購読をこの小さなコンポーネントに閉じ込めることで、タブ切り替え時に
 * 親 (AppLayout) が再レンダーしない。`chatView` は AppLayout が一度だけ生成した要素を
 * そのまま受け取るため参照が変わらず、React はチャットのサブツリー (全メッセージの
 * Markdown 描画) の再レンダーをスキップする。チャットビューは非表示時もマウントを
 * 維持し、スクロール位置や取得済みデータを保つ。
 */
function MainViewSwitcher({ chatView, views }: MainViewSwitcherProps) {
	const mainView = useLayoutStore((s) => s.mainView);
	return (
		<>
			<div
				style={{
					flex: 1,
					minWidth: 0,
					display: mainView === "chats" ? "block" : "none",
				}}
			>
				{chatView}
			</div>
			{mainView !== "chats" && <div style={{ flex: 1, minWidth: 0 }}>{views[mainView]}</div>}
		</>
	);
}

function ResizeHandle() {
	return <PanelResizeHandle className="izanagi-resize-handle" />;
}
